import React from 'react';
import { Navigate } from 'react-router-dom';
import { useFeature, useFeaturesLoading } from '@/lib/features/FeatureProvider';

interface FeatureGateProps {
  /** The feature flag name to check. */
  feature: string;
  /** Content to render when the feature is enabled. */
  children: React.ReactNode;
  /**
   * What to render when the feature is disabled.
   * - 'hide' (default): render nothing
   * - 'redirect': navigate to /dashboard
   * - 'message': show a "Feature not available" message
   * - ReactNode: render that node instead
   */
  fallback?: 'hide' | 'redirect' | 'message' | React.ReactNode;
}

/**
 * Conditionally renders children based on a tenant feature flag.
 *
 * Usage:
 *   <FeatureGate feature="portal_management">
 *     <PortalManagementPage />
 *   </FeatureGate>
 *
 * For route guarding, use fallback="redirect" or fallback="message":
 *   <FeatureGate feature="theming" fallback="redirect">
 *     <ThemeSettingsPage />
 *   </FeatureGate>
 */
export function FeatureGate({ feature, children, fallback = 'hide' }: FeatureGateProps) {
  const enabled = useFeature(feature);
  const loading = useFeaturesLoading();

  // While loading, render nothing — prevents flash of feature-gated content
  if (loading) return null;

  if (enabled) return <>{children}</>;

  // Feature is disabled — apply fallback
  if (fallback === 'hide') return null;

  if (fallback === 'redirect') return <Navigate to="/dashboard" replace />;

  if (fallback === 'message') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground mb-2">Feature not available</h2>
          <p className="text-muted-foreground">
            This feature is not enabled for your account. Contact your administrator to enable it.
          </p>
        </div>
      </div>
    );
  }

  // Custom fallback node
  return <>{fallback}</>;
}
